import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../../lib/api";
import { Empty, Field, Loader } from "../common";
import { ImageUpload } from "../ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const PLACES = { login: "Página de login", home: "Página de início" };

const AdForm = ({ open, onOpenChange, ad, onSaved }) => {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(ad || { title: "", image_path: null, link: "", text: "", placements: ["login", "home"], active: true, order: 0 }); }, [open, ad]);
  const togglePlace = (p) => setF({ ...f, placements: f.placements.includes(p) ? f.placements.filter((x) => x !== p) : [...f.placements, p] });
  const save = async (e) => {
    e.preventDefault();
    if (!f.placements.length) return toast.error("Escolha onde exibir");
    try {
      const body = { ...f, order: Number(f.order || 0) };
      if (ad) await api.put(`/ads/${ad.id}`, body); else await api.post("/ads", body);
      toast.success("Publicidade salva"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (!f.placements) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-md">
        <DialogHeader><DialogTitle className="h-display text-3xl">{ad ? "Editar publicidade" : "Nova publicidade"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Banner (ideal 1600x500)"><ImageUpload wide value={f.image_path} onChange={(v) => setF({ ...f, image_path: v })} testid="ad-image-upload" label="Enviar banner" /></Field>
          <Field label="Título"><input required data-testid="ad-title-input" className="inp" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Texto (opcional, aparece sobre o banner)"><input data-testid="ad-text-input" className="inp" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} /></Field>
          <Field label="Link"><input data-testid="ad-link-input" className="inp" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} placeholder="https://" /></Field>
          <Field label="Exibir em">
            <div className="flex gap-2">
              {Object.entries(PLACES).map(([k, l]) => (
                <button type="button" key={k} data-testid={`ad-place-${k}`} onClick={() => togglePlace(k)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${f.placements.includes(k) ? "bg-[#E4F222] text-black" : "border border-[#232B3E] text-[#94A3B8]"}`}>{l}</button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ordem"><input type="number" data-testid="ad-order-input" className="inp" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} /></Field>
            <Field label="Status">
              <select data-testid="ad-active-select" className="inp" value={f.active ? "1" : "0"} onChange={(e) => setF({ ...f, active: e.target.value === "1" })}><option value="1">Ativo</option><option value="0">Oculto</option></select>
            </Field>
          </div>
          <button data-testid="ad-save-btn" className="btn-lime w-full rounded-full py-3">Salvar</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdsManager = () => {
  const [list, setList] = useState(null);
  const [dlg, setDlg] = useState({ open: false, ad: null });
  const load = () => api.get("/ads", { params: { all: true } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const del = async (a) => { if (!window.confirm(`Excluir ${a.title}?`)) return; await api.delete(`/ads/${a.id}`); load(); };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-[#94A3B8]">Banners em carrossel abaixo do botão do Google e na página de início.</p>
        <button data-testid="new-ad-btn" onClick={() => setDlg({ open: true, ad: null })} className="btn-lime flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" />Adicionar</button>
      </div>
      {!list ? <Loader /> : list.length === 0 ? <Empty icon={ImageIcon} title="Sem publicidade" /> : (
        <div className="grid gap-3 sm:grid-cols-2">
          {list.map((a) => (
            <div key={a.id} data-testid={`ad-row-${a.id}`} className="card-x overflow-hidden">
              <div className="flex h-28 items-center justify-center bg-[#0B0E14]">{a.image_path ? <img src={fileUrl(a.image_path)} alt="" className="h-full w-full object-cover" /> : <span className="h-display text-xl">{a.title}</span>}</div>
              <div className="flex items-center gap-2 p-3">
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{a.title}</div><div className="text-[11px] text-[#64748B]">{a.placements.map((p) => PLACES[p]).join(" · ")}</div></div>
                {a.active ? <Eye className="h-4 w-4 text-[#E4F222]" /> : <EyeOff className="h-4 w-4 text-[#64748B]" />}
                <button data-testid={`edit-ad-${a.id}`} onClick={() => setDlg({ open: true, ad: a })} className="p-1.5 text-[#94A3B8]"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-ad-${a.id}`} onClick={() => del(a)} className="p-1.5 text-[#94A3B8] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <AdForm open={dlg.open} ad={dlg.ad} onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />
    </div>
  );
};
